import axios from 'axios';

// Improved response interceptor with more debugging info
axios.interceptors.response.use(
  response => {
    console.log(`✓ ${response.config.method.toUpperCase()} ${response.config.url} success:`, response.status);
    
    // Log response headers for debugging cache issues
    console.log("Response headers:", response.headers);
    
    if (response.config.url.includes('/api/marketing/plans/') && 
        (response.config.method === 'put' || response.config.method === 'post')) {
      console.log("Marketing plan endpoint response data:", response.data);
    }
    
    return response;
  },
  async error => {
    const originalRequest = error.config;
    
    // If token expired and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await fetch('/api/auth/token/refresh/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              refresh: refreshToken
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            localStorage.setItem('jwtToken', data.access);
            
            // Retry original request with new token
            originalRequest.headers['Authorization'] = `Bearer ${data.access}`;
            return axios(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }
      
      // If refresh fails, clear storage and redirect to login
      localStorage.clear();
      window.location.href = '/login';
    }
    
    console.error(`✗ ${error.config?.method?.toUpperCase()} ${error.config?.url} failed:`, error.response?.status);
    console.error("Error details:", {
      message: error.message,
      responseData: error.response?.data,
      status: error.response?.status
    });
    
    // Log response headers even for errors
    if (error.response) {
      console.error("Error response headers:", error.response.headers);
    }
    
    return Promise.reject(error);
  }
);

// Improved request interceptor with more debugging info
axios.interceptors.request.use(
  config => {
    console.log(`→ ${config.method.toUpperCase()} request to ${config.url}`);
    
    // Log request headers
    console.log("Request headers:", config.headers);
    
    // Add cache busting for marketing plans API
    if (config.url.includes('/api/marketing/plans/')) {
      // Add cache control headers
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';
      
      // Add a timestamp parameter to avoid caching
      const separator = config.url.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}_ts=${new Date().getTime()}`;
    }
    
    // Create a unique key for this request
    const requestKey = `${config.method}:${config.url}`;
    
    // Cancel any pending duplicate requests
    if (pendingRequests[requestKey]) {
      console.log(`Cancelling duplicate request: ${requestKey}`);
      pendingRequests[requestKey].cancel('Duplicate request cancelled');
    }
    
    // Create a new cancel token source
    const source = axios.CancelToken.source();
    config.cancelToken = source.token;
    pendingRequests[requestKey] = source;
    
    return config;
  },
  error => Promise.reject(error)
);

// Add deduplication tracking
const pendingRequests = {};

axios.interceptors.response.use(
  response => {
    // Remove the request from pending once it's done
    const requestKey = `${response.config.method}:${response.config.url}`;
    delete pendingRequests[requestKey];
    return response;
  },
  error => {
    // Also remove from pending on error
    if (error.config) {
      const requestKey = `${error.config.method}:${error.config.url}`;
      delete pendingRequests[requestKey];
    }
    return Promise.reject(error);
  }
);

// Tambahkan ini untuk URL marketing plans
window.addEventListener('beforeunload', () => {
  // Clear localStorage marketing cache before refresh
  localStorage.removeItem('marketingPlansCache');
  // Add flag to indicate we're refreshing the page
  sessionStorage.setItem('pageRefreshing', 'true');
});

window.addEventListener('load', () => {
  if (sessionStorage.getItem('pageRefreshing') === 'true') {
    console.log('Page was refreshed, cache cleared');
    sessionStorage.removeItem('pageRefreshing');
    
    // If on marketing plans page, reload data
    if (window.location.pathname.includes('marketing-plans') || 
        window.location.pathname.includes('dashboard')) {
      console.log('On relevant page, triggering data reload');
      // Dispatch a custom event that components can listen for
      window.dispatchEvent(new CustomEvent('forceDataRefresh'));
    }
  }
});

// Add event listener for forced reload in components
export const addForceRefreshListener = (callback) => {
  window.addEventListener('forceDataRefresh', callback);
  return () => window.removeEventListener('forceDataRefresh', callback);
};

export default axios;